import { useTheme } from '@emotion/react';
import { Formik, FormikProps } from 'formik';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';

import {
  Alert,
  Box,
  EntryList,
  FieldError,
  Flex,
  Hint,
  SearchAddress,
  Text,
  TextField,
} from '~/components/blocks';
import { TextSpan } from '~/components/blocks/TextSpan';
import { Geocoding, Map } from '~/components/partials';
import { FormErrorMessage } from '~/constants/messages';
import { numberOrHyphenRegexp } from '~/constants/regexp';
import { toHalfNumberWithHyphen } from '~/utils/convert';

import { Fields } from './types';
import { validationSchema } from './validation';

type Props = {
  initialValue: Fields;
  loading: boolean;
  slug: string;
  pharmacyCodeEditDisabled: boolean;
  onClickGeocoding: () => void;
  onSubmit: (values: Fields) => void;
  uberSettingEnable: boolean;
  availableUber: boolean;
};

export const BaseForm = forwardRef<FormikProps<Fields>, Props>((props, ref) => {
  const theme = useTheme();
  const {
    loading,
    initialValue,
    slug,
    onClickGeocoding,
    onSubmit,
    uberSettingEnable,
    availableUber,
    pharmacyCodeEditDisabled,
  } = props;
  const [previousValue, setPreviousValue] = useState<Fields>(initialValue);
  const [map, setMap] = useState({
    latitude: initialValue.latitude,
    longitude: initialValue.longitude,
  });
  const [geocodingError, setGeocodingError] = useState<string | null>(null);

  const handleValueChanged = useCallback(
    (values: Fields) => {
      if (
        previousValue.postalCode !== values.postalCode ||
        previousValue.prefecture !== values.prefecture ||
        previousValue.city !== values.city ||
        previousValue.line !== values.line
      ) {
        setGeocodingError(FormErrorMessage.map);
      } else {
        setGeocodingError(null);
      }
    },
    [previousValue, setGeocodingError],
  );

  const handleBlurWithConvert = useCallback(
    (formik: FormikProps<Fields>, name: string, e: React.FocusEvent<HTMLInputElement>) => {
      if (!numberOrHyphenRegexp.test(e.currentTarget.value)) return;
      formik.setFieldValue(name, toHalfNumberWithHyphen(e.currentTarget.value));
    },
    [],
  );

  const handleChange = useCallback(
    (formik: FormikProps<Fields>) => {
      return (e: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange(e);
        handleValueChanged(formik.values);
      };
    },
    [handleValueChanged],
  );

  useEffect(() => {
    setGeocodingError(null);
    setPreviousValue(initialValue);
  }, [initialValue]);

  return (
    <Formik
      enableReinitialize
      innerRef={ref}
      initialValues={initialValue}
      validationSchema={validationSchema}
      onSubmit={(values, formikHelper) => {
        if (geocodingError) return;
        // 地図を動かす度に値を書き換えてしまうと地図が変な挙動になるため、submit直前に入れる
        formikHelper.setFieldValue('latitude', map.latitude);
        formikHelper.setFieldValue('longitude', map.longitude);
        onSubmit({ ...values, ...map });
      }}
    >
      {(formik) => (
        <Box padding={theme.space.xl}>
          <EntryList>
            <EntryList.Head>店舗コード</EntryList.Head>
            <EntryList.Body>{slug}</EntryList.Body>
          </EntryList>
          <EntryList>
            <EntryList.Head>店舗名</EntryList.Head>
            <EntryList.Body>
              <TextField
                disabled={loading}
                error={formik.errors.name}
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                placeholder="メドレー薬局 六本木一丁目店"
              />
            </EntryList.Body>
          </EntryList>
          <EntryList>
            <EntryList.Head>
              <Flex alignItems="center">
                薬局コード
                {pharmacyCodeEditDisabled && (
                  <Hint placement="top" marginLeft={theme.space.s}>
                    処方箋読取設定の個別設定が有効な間は
                    <br />
                    薬局コードを変更できません
                  </Hint>
                )}
              </Flex>
            </EntryList.Head>
            <EntryList.Body>
              <TextField
                disabled={loading || pharmacyCodeEditDisabled}
                error={formik.errors.pharmacyCode}
                name="pharmacyCode"
                value={formik.values.pharmacyCode || ''}
                onChange={formik.handleChange}
                placeholder="0123456789"
              />
            </EntryList.Body>
          </EntryList>
          <EntryList>
            <EntryList.Head>電話番号</EntryList.Head>
            <EntryList.Body>
              <TextField
                disabled={loading}
                error={formik.errors.tel}
                name="tel"
                type="tel"
                value={formik.values.tel}
                onChange={formik.handleChange}
                onBlur={(e) => handleBlurWithConvert(formik, 'tel', e)}
                placeholder="0312341234"
              />
            </EntryList.Body>
          </EntryList>
          <EntryList>
            <EntryList.Head>FAX</EntryList.Head>
            <EntryList.Body>
              <TextField
                disabled={loading}
                error={formik.errors.fax}
                name="fax"
                type="tel"
                value={formik.values.fax}
                onChange={formik.handleChange}
                onBlur={(e) => handleBlurWithConvert(formik, 'fax', e)}
                placeholder="0312341234"
              />
            </EntryList.Body>
          </EntryList>
          <EntryList>
            <EntryList.Head>郵便番号</EntryList.Head>
            <EntryList.Body>
              <SearchAddress
                disabled={loading}
                initialPostalCode={initialValue.postalCode}
                onChange={(address) => {
                  const newValues = { ...formik.values, ...address };
                  formik.setValues(newValues);
                  handleValueChanged(newValues);
                }}
                placeholder="106-6222"
              />
            </EntryList.Body>
          </EntryList>
          <EntryList>
            <EntryList.Head>都道府県</EntryList.Head>
            <EntryList.Body>
              {formik.values.prefecture ? (
                <Text>{formik.values.prefecture}</Text>
              ) : (
                <Text color="pink">郵便番号から検索</Text>
              )}
            </EntryList.Body>
          </EntryList>
          <EntryList>
            <EntryList.Head>市区町村</EntryList.Head>
            <EntryList.Body>
              {formik.values.city ? (
                <Text>{formik.values.city}</Text>
              ) : (
                <Text color="pink">郵便番号から検索</Text>
              )}
            </EntryList.Body>
          </EntryList>
          <EntryList>
            <EntryList.Head>番地・マンション名</EntryList.Head>
            <EntryList.Body>
              <TextField
                disabled={loading}
                error={formik.errors.line}
                name="line"
                value={formik.values.line}
                onChange={handleChange(formik)}
                placeholder="1-2-3 六本木グランドタワー"
              />
            </EntryList.Body>
          </EntryList>
          {uberSettingEnable && availableUber && (
            <Alert status="warning" marginTop={theme.space.l}>
              <Text size="m">
                現住所は当日配達
                <TextSpan size="s" text="（Uber&nbsp;Eats）" />
                サービスエリア圏内です。住所変更後はエリアの再審査をするため、
                <b>一時的に機能が利用できなくなります。あらかじめご了承下さい</b>
              </Text>
            </Alert>
          )}
          <EntryList mt={theme.space.l}>
            <EntryList.Head>地図</EntryList.Head>
            <EntryList.Body>
              <Box mb={theme.space.m}>
                <Geocoding
                  prefecture={formik.values.prefecture}
                  city={formik.values.city}
                  line={formik.values.line}
                  onChange={(values) => {
                    // Geocoding -> 地図移動 -> Geocoding順で実行(Geocodingの結果が同じ)では
                    // 地図移動の結果がformikに反映されていないため、地図は再描画されない
                    // そのため、Geocodingの結果を反映する前に、地図の緯度経度をセットする
                    formik.setFieldValue('latitude', map.latitude);
                    formik.setFieldValue('longitude', map.longitude);

                    // Geocodingの結果を反映
                    formik.setFieldValue('latitude', values.lat);
                    formik.setFieldValue('longitude', values.lng);
                    onClickGeocoding();
                    setPreviousValue(formik.values);
                  }}
                  onError={setGeocodingError}
                />
                {geocodingError && <FieldError error={geocodingError} fontSize="s" />}
              </Box>
              <Box>
                <Map
                  width={270}
                  height={200}
                  latitude={formik.values.latitude !== 0 ? formik.values.latitude : undefined}
                  longitude={formik.values.longitude !== 0 ? formik.values.longitude : undefined}
                  onChange={(values) => {
                    setMap({ latitude: values.lat, longitude: values.lng });
                  }}
                />
                <Text size="xs" marginTop={theme.space.m}>
                  ピンの位置が異なる場合は、詳細な位置にピンを移動してください。
                </Text>
              </Box>
            </EntryList.Body>
          </EntryList>
        </Box>
      )}
    </Formik>
  );
});

BaseForm.displayName = 'BaseForm';
